import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@apollo/orbit';
import { range } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router';

import { ProposalCommentButton } from 'src/app/graph/proposalLayout/ProposalCommentButton';
import { proposalEditorRouteConfig } from 'src/app/graph/routes';
import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

const VISIBLE_RANGE_PADDING = 5;

export const EditorCommentButtons = ({
  editor,
  commentsByLineNumber,
  schemaScope,
  addedModifiedLines,
  editorTextValue,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  commentsByLineNumber: Record<
    number,
    GraphQLTypes.ProposalEditorQuery_service_variant_proposal_parentComments_ParentChangeProposalComment[]
  >;
  schemaScope: string | undefined;
  addedModifiedLines: number[];
  editorTextValue: string | undefined;
}) => {
  const [visibleRange, setVisibleRange] = React.useState<
    monacoEditor.Range | undefined
  >(editor.getVisibleRanges()[0]);

  React.useEffect(() => {
    const disposables = [
      editor.onDidScrollChange(() => {
        const visibleRanges = editor.getVisibleRanges();

        setVisibleRange(visibleRanges[0]);
      }),
    ];

    return () => {
      disposables.forEach((disposable) => disposable?.dispose());
    };
  }, [editor]);

  return (
    <>
      {range(
        visibleRange?.startLineNumber ?? 0,
        (visibleRange?.endLineNumber ?? 0) + VISIBLE_RANGE_PADDING,
      ).map((lineNumber) => {
        const comments = commentsByLineNumber[lineNumber] ?? [];

        if (
          isMinLengthArray(1, comments) &&
          !addedModifiedLines.includes(lineNumber)
        ) {
          return (
            <EditorCommentButton
              editorTextValue={editorTextValue}
              key={lineNumber}
              editor={editor}
              lineNumber={lineNumber}
              schemaScope={schemaScope}
              comments={comments}
            />
          );
        }

        return null;
      })}
    </>
  );
};

const EditorCommentButton = ({
  editor,
  lineNumber,
  comments,
  schemaScope,
  editorTextValue,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  lineNumber: number;
  comments: GraphQLTypes.ProposalEditorQuery_service_variant_proposal_parentComments_ParentChangeProposalComment[];
  schemaScope: string | undefined;
  editorTextValue: string | undefined;
}) => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const marginDomNode = React.useMemo(() => document.createElement('div'), []);
  const domNode = React.useMemo(() => document.createElement('div'), []);

  const currentViewZone = React.useRef<{
    zoneId: string;
    zone: monacoEditor.editor.IViewZone;
  }>();

  const history = useHistory();
  const location = useLocation();

  const handleClick = useCallback(
    (coordinate: string | undefined, commentId: string | undefined) => {
      history.push(
        proposalEditorRouteConfig.locationFrom({
          location,
          patch: {
            selectedSchema: schemaScope,
            linkedCoordinateName: coordinate,
            linkedCommentId: commentId,
          },
        }),
      );
    },
    [history, location, schemaScope],
  );

  const afterLineNumber = lineNumber - 1;

  React.useEffect(() => {
    // A view zone is a full horizontal rectangle that 'pushes' text down. The editor reserves space for view zones when rendering.
    // we position ours absolutely so that they don't push anything down and are just rendered over a line
    editor.changeViewZones((changeAccessor) => {
      marginDomNode.className = 'z-20';
      const newZone = {
        afterLineNumber,
        heightInLines: 0,
        // Possibly tie menu content portal to the div here
        domNode,
        marginDomNode,
      };
      currentViewZone.current = {
        zone: newZone,
        zoneId: changeAccessor.addZone(newZone),
      };
    });

    return () => {
      const lastZone = currentViewZone.current;
      if (!lastZone) return;
      editor.changeViewZones((changeAccessor) => {
        changeAccessor.removeZone(lastZone.zoneId);
      });
      currentViewZone.current = undefined;
    };
    // when models change, view zones are lost so it needs to be in the dependency array
  }, [afterLineNumber, domNode, editor, marginDomNode, editorTextValue]);

  const isMultiThreadLine = isMinLengthArray(2, comments);
  return (
    <>
      {createPortal(
        <div className="h-5">
          {/* Use menu instead of popover */}
          <WrapMaybe
            element={
              <Popover
                isLazy
                placement="end"
                isOpen={isOpen}
                onClose={onClose}
              />
            }
            on={isMultiThreadLine}
          >
            <>
              <WrapMaybe
                // Popover is passed a child by WrapMaybe that overrides the one passed here
                // eslint-disable-next-line react/no-children-prop
                element={<PopoverTrigger children={undefined} />}
                on={isMultiThreadLine}
              >
                <ProposalCommentButton
                  // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
                  // TODO(Maya) add link to synthetics test
                  data-testid={`proposals_add_comment_button_editor_${comments[0]?.schemaCoordinate}`}
                  replyCount={comments[0] ? comments[0].replyCount + 1 : 1}
                  isMultiThreadLine={isMultiThreadLine}
                  {...(!isMultiThreadLine
                    ? {
                        onClick: () =>
                          handleClick(
                            comments[0]?.schemaCoordinate,
                            comments[0]?.id,
                          ),
                      }
                    : { onClick: onToggle })}
                />
              </WrapMaybe>
              {isMultiThreadLine &&
                createPortal(
                  <PopoverContent className="ml-1 max-w-60 p-2">
                    {comments
                      .sort((a, b) =>
                        a.schemaCoordinate.localeCompare(b.schemaCoordinate),
                      )
                      .map((comment, i) => (
                        <Button
                          variant="hidden"
                          size="sm"
                          key={comment.id}
                          className="w-full justify-between"
                          onClick={() => {
                            onClose();
                            handleClick(comment.schemaCoordinate, comment.id);
                          }}
                        >
                          <div className="min-w-0 truncate">
                            <span className="truncate font-semibold">
                              Thread {i + 1}:
                            </span>{' '}
                            {comment.schemaCoordinate}
                          </div>
                          <div className="ml-2">{comment.replyCount + 1}</div>
                        </Button>
                      ))}
                  </PopoverContent>,
                  domNode,
                )}
            </>
          </WrapMaybe>
        </div>,
        marginDomNode,
      )}
    </>
  );
};
